// Rangliste (R-09): wer hat bei einer Kennzahl vorn. Wie in Close erst ab zwei Personen mit Daten, sonst ein Hinweis.
import { useMemo, useState } from "react";
import { berichteApi, type BerichtEingabe, type Kennzahl } from "../api/berichte";
import { Avatar } from "../components/Bausteine";
import { Icon } from "../components/Icon";
import { MenueKnopf } from "../components/Menue";
import { useGeladen } from "./daten";
import { wertText } from "./format";

const STANDARD = "opps_gewonnen";

export function Rangliste({ eingabe, schluessel, katalog }: { eingabe: BerichtEingabe; schluessel: string; katalog: Kennzahl[] }) {
  const [id, setId] = useState(katalog.some((k) => k.id === STANDARD) ? STANDARD : katalog[0]?.id);
  const kennzahl = katalog.find((k) => k.id === id);
  const { daten, laedt } = useGeladen(
    () => berichteApi.abfrage({ ...eingabe, vergleich: undefined, kennzahlen: [id!], gruppieren: "nutzer" }),
    `${schluessel}|${id}`,
    !!id,
  );
  const zeilen = useMemo(
    () =>
      (daten?.gruppen ?? [])
        .map((g) => ({ ...g, wert: g.werte[id!] ?? 0 }))
        .filter((g) => (g.wert ?? 0) > 0)
        .sort((a, b) => (b.wert ?? 0) - (a.wert ?? 0)),
    [daten, id],
  );
  const max = Math.max(1, ...zeilen.map((z) => z.wert ?? 0));
  const gruppiert = useMemo(() => {
    const nach = new Map<string, Kennzahl[]>();
    for (const k of katalog) nach.set(k.gruppe, [...(nach.get(k.gruppe) ?? []), k]);
    return [...nach.entries()];
  }, [katalog]);
  return (
    <section className="rangliste" aria-label="Rangliste" aria-busy={laedt}>
      <header className="rangliste__kopf">
        <div className="rangliste__titel">Rangliste</div>
        <MenueKnopf
          knopfKlasse="kachel__titel"
          titel="Kennzahl der Rangliste wählen"
          breite={280}
          punkte={gruppiert.flatMap(([gruppe, liste]) => [
            { label: gruppe, ueberschrift: true },
            ...liste.map((k) => ({ label: `${k.titel} · ${(k.untertitel ?? "").toLocaleLowerCase("de")}`, icon: k.id === id ? ("haken" as const) : undefined, onClick: () => setId(k.id) })),
          ])}
        >
          <span className="kachel__titeltext">{kennzahl?.titel}</span>
          <Icon name="chevron-unten" groesse={12} />
        </MenueKnopf>
        <div className="kachel__unter">{kennzahl?.untertitel}</div>
      </header>
      {zeilen.length < 2 ? (
        <p className="leise rangliste__leer">Für eine Rangliste fehlen Daten: es braucht mindestens zwei Personen mit Werten im Zeitraum.</p>
      ) : (
        <ol className="rangliste__liste">
          {zeilen.slice(0, 8).map((z, i) => (
            <li key={z.schluessel}>
              <span className="rangliste__platz">{i + 1}</span>
              <Avatar name={z.titel} id={z.schluessel} groesse={20} />
              <span className="rangliste__name" title={z.titel}>{z.titel}</span>
              <span className="rangliste__bahn" aria-hidden="true">
                <span style={{ width: `${((z.wert ?? 0) / max) * 100}%` }} />
              </span>
              <span className="rangliste__wert">{wertText(z.wert, kennzahl?.einheit ?? "anzahl")}</span>
            </li>
          ))}
        </ol>
      )}
    </section>
  );
}
